<nav compact tuiSubheader [style.position]="'sticky'" class="bg-white">
  <tui-breadcrumbs [itemsLimit]="10">
    <ng-container *ngFor="let item of breadcrumbs; let last = last">
      <ng-container *ngIf="last; else notLast">
        <strong *tuiItem tuiFade>{{ item.label }}</strong>
      </ng-container>
      <ng-template #notLast>
        <button *tuiItem tuiLink type="button" (click)="navigate(item.link)" [disabled]="!item.link">
          {{ item.label }}
        </button>
      </ng-template>
    </ng-container>
  </tui-breadcrumbs>
</nav>

<tui-loader [overlay]="true" [showLoader]="isLoading" class="w-full block min-h-[400px]">
  <section class="w-full mt-4">
    <p class="text-[1.75rem] font-bold text-[#000000A6] m-0">
      {{ leaveId ? "Edit Leave" : "Add New Leave" }}
    </p>

    <form [formGroup]="leaveForm" class="mt-8" (ngSubmit)="saveLeave()">
      <div class="flex flex-col xl:flex-row gap-8 xl:gap-12">
        <!-- Left label column -->
        <div class="xl:w-[220px] shrink-0">
          <p class="text-base font-medium text-[#3E4A58] my-0">Leave Information</p>
          <p class="text-sm font-normal text-[#949494D9] my-0 mt-1">
            Enter leave details for the selected nurse
          </p>
        </div>

        <!-- Form fields -->
        <div class="flex-1 max-w-xl">
          <label tuiLabel class="block mb-4">
            <tui-combo-box formControlName="nurse" [stringify]="stringifyNurse" [(search)]="search" [strictMatcher]="null">
              Name
              <input tuiTextfieldLegacy placeholder="Select nurse" />
              <tui-data-list-wrapper *tuiDataList [items]="filteredNurses"
                [itemContent]="stringifyNurse | tuiStringifyContent" />
            </tui-combo-box>
            <tui-error formControlName="nurse" class="mb-1" [error]="[] | tuiFieldError | async" />
          </label>

          <div *ngIf="leaveForm.get('nurse')?.value as selectedNurse" class="mb-5 space-y-1 pl-1">
            <ng-container *ngIf="selectedNurse.employmentType === 'Permanent'; else casualNurse">
              <p class="text-sm text-[#595959] m-0">
                EPF Number :
                <span class="font-semibold text-[#191C30E5]">{{ employeeNumberDisplay }}</span>
              </p>
              <p class="text-sm text-[#595959] m-0">
                Appointment Date :
                <span class="font-semibold text-[#191C30E5]">{{ (selectedNurse.appointmentDate ? (selectedNurse.appointmentDate | date : "MMM d, y") : "—") }}</span>
              </p>
            </ng-container>
            <ng-template #casualNurse>
              <p class="text-sm text-[#595959] m-0">
                Casual ID :
                <span class="font-semibold text-[#191C30E5]">{{ employeeNumberDisplay }}</span>
              </p>
              <p class="text-sm text-[#595959] m-0">
                Actual Join Date :
                <span class="font-semibold text-[#191C30E5]">{{ (selectedNurse.registrationDate ? (selectedNurse.registrationDate | date : "MMM d, y") : "—") }}</span>
              </p>
            </ng-template>
          </div>

          <label tuiLabel class="block mb-4">
            <tui-input-date formControlName="submittedDate">
              Submitted Date
              <input tuiTextfieldLegacy />
            </tui-input-date>
          </label>

          <label tuiLabel class="block mb-4">
            <tui-combo-box formControlName="leaveType" [stringify]="stringifyLeaveType">
              Leave Type
              <input tuiTextfieldLegacy placeholder="Leave Type" />
              <tui-data-list-wrapper *tuiDataList [items]="leaveTypeOptions"
                [itemContent]="stringifyLeaveType | tuiStringifyContent" />
            </tui-combo-box>
            <tui-error formControlName="leaveType" [error]="[] | tuiFieldError | async" />
          </label>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <label tuiLabel>
              <tui-input-date formControlName="fromDate">
                Leave Apply From
                <input tuiTextfieldLegacy />
              </tui-input-date>
              <tui-error formControlName="fromDate" [error]="[] | tuiFieldError | async" />
            </label>
            <label tuiLabel>
              <tui-input-date formControlName="toDate">
                Leave Apply To
                <input tuiTextfieldLegacy />
              </tui-input-date>
            </label>
          </div>

          <div *ngIf="expandedDates.length" class="mb-4">
            <p class="text-sm font-semibold mb-2 text-[#3E4A58]">
              Leave Period — {{ expandedDates.length }} day(s)
            </p>
            <div class="flex flex-wrap gap-2">
              <span *ngFor="let d of expandedDates" class="px-2 py-1 text-xs rounded bg-[#E5EDFF] text-[#285CA5]">
                {{ d }}
              </span>
            </div>
          </div>

          <label tuiLabel class="block mb-4">
            <tui-textarea formControlName="reason">Reason</tui-textarea>
          </label>

          <div class="flex justify-end gap-3 mt-8">
            <button tuiButton type="button" appearance="secondary" size="m" routerLink="/admin/leave"
              style="border: 2px solid #e5e7ea">
              Cancel
            </button>
            <button tuiButton type="submit" size="m">
              {{ leaveId ? "Save Changes" : "Add New Leave" }}
            </button>
          </div>
        </div>

        <!-- Right balance card -->
        <div class="xl:w-[280px] shrink-0">
          <div class="balance-card">
            <p class="text-base font-semibold text-[#3E4A58] m-0 mb-3">Leave Information</p>
            <ng-container *ngIf="balance; else noBalance">
              <!-- Permanent Nurse Breakdown -->
              <ng-container *ngIf="balance.annualLeave?.eligible; else casualBreakdown">
                <div class="balance-row">
                  <span>Available Day-offs</span>
                  <strong>{{ balance.dayOffs?.entitled ?? 0 }}</strong>
                </div>
                <div class="balance-row">
                  <span>Available Annual Leaves</span>
                  <strong>{{ balance.annualLeave?.entitled ?? 0 }}</strong>
                </div>
                <div class="balance-row">
                  <span>Used Day-offs</span>
                  <strong>{{ balance.dayOffs?.used ?? 0 }}</strong>
                </div>
                <div class="balance-row">
                  <span>Used Annual Leaves</span>
                  <strong>{{ balance.annualLeave?.used ?? 0 }}</strong>
                </div>
                <div class="balance-row">
                  <span>Remaining Day-offs</span>
                  <strong>{{ balance.dayOffs?.remaining ?? 0 }}</strong>
                </div>
                <div class="balance-row">
                  <span>Remaining Annual Leaves</span>
                  <strong>{{ balance.annualLeave?.remaining ?? 0 }}</strong>
                </div>
              </ng-container>

              <!-- Casual Nurse Breakdown -->
              <ng-template #casualBreakdown>
                <div class="balance-row">
                  <span>Available leave count</span>
                  <strong>{{ availableCount }}</strong>
                </div>
                <div class="balance-row">
                  <span>Already used leave count</span>
                  <strong>{{ usedCount }}</strong>
                </div>
                <div class="balance-row">
                  <span>Remaining leave balance</span>
                  <strong>{{ remainingCount }}</strong>
                </div>
              </ng-template>
              <p class="text-xs text-[#6B6D80] mt-3 mb-0" *ngIf="balance.annualLeave?.eligible">
                Includes Day-Offs (month) and Annual Leave (year).
              </p>
              <p class="text-xs text-[#6B6D80] mt-3 mb-0" *ngIf="!balance.annualLeave?.eligible">
                Casual nurses — Day-Offs only (5 / month).
              </p>
              <p class="text-xs text-[#285CA5] mt-2 mb-0" *ngIf="preview?.deduction as d">
                This request will use {{ d.dayOffsUsed }} day-off(s)
                <span *ngIf="d.annualLeaveUsed">
                  + {{ d.annualLeaveUsed }} annual day(s)
                </span>.
              </p>
              <p class="text-xs text-red-600 mt-2 mb-0" *ngIf="preview && preview.valid === false">
                {{ preview.message }}
              </p>
            </ng-container>
            <ng-template #noBalance>
              <p class="text-sm text-[#6B6D80] m-0">
                Select a nurse to view available leave balance.
              </p>
            </ng-template>
          </div>
        </div>
      </div>
    </form>

    <div *ngIf="showConflictDialog" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div class="bg-white rounded-lg p-6 max-w-md shadow-lg mx-4">
        <p class="text-base font-semibold mb-3 text-[#191C30E5]">
          An existing shift has been scheduled for this nurse on the selected leave date(s).
          Please choose an action to proceed.
        </p>
        <div class="flex gap-3 mt-4 justify-end">
          <button tuiButton type="button" appearance="secondary" size="s" (click)="keepShift()">
            Keep Shift
          </button>
          <button tuiButton type="button" size="s" (click)="removeShiftAndSave()">
            Remove Shift
          </button>
        </div>
      </div>
    </div>
  </section>
</tui-loader>